import type { Person as BasePerson } from '@mes-amis/super-form';

// Person data type — extends the shared package type with app-specific fields
export interface Person extends BasePerson {
  created_at?: string;
  updated_at?: string;
  secondary_languages?: string[];
  role_ids?: {
    client?: string;
    volunteer?: string | null;
  };
}

/**
 * The person's official name — mirrors monami's HasFullName#official_full_name
 * (legal first + last, never the preferred name). This is the name signatures
 * are matched against EXACTLY server-side, so signing flows must use this and
 * not decoratePerson's preferred-name-first `fullName`.
 */
export function officialFullName(
  person?: { official_full_name?: string | null; first_name?: string | null; last_name?: string | null } | null
): string {
  if (!person) return '';
  return person.official_full_name || [person.first_name, person.last_name].filter(Boolean).join(' ');
}

/**
 * Decorator function that adds computed properties to a Person
 * Returns the person with fullName and initials getters
 */
export function decoratePerson<T extends Person>(person: T, fallback = '') {
  return {
    ...person,
    get fullName() {
      const firstName = person.preferred_name || person.first_name || '';
      const lastName = person.last_name || '';
      return `${firstName} ${lastName}`.trim() || fallback;
    },
    /**
     * Display name showing the legal first + last name,
     * with the preferred name in quotes when it differs from first_name.
     * e.g. `John "Johnny" Doe` or just `John Doe` when no preferred name.
     */
    get displayName() {
      const firstName = this.first_name || '';
      const middleName = this.middle_name || '';
      const lastName = this.last_name || '';
      const preferred = this.preferred_name;
      const hasDistinctPreferred = preferred && preferred !== firstName;

      const parts: string[] = [];
      if (firstName) parts.push(firstName);
      if (hasDistinctPreferred) parts.push(`"${preferred}"`);
      if (middleName) parts.push(middleName);
      if (lastName) parts.push(lastName);
      return parts.join(' ') || fallback;
    },
    get initials() {
      const firstName = person.preferred_name || person.first_name || '';
      const lastName = person.last_name || '';
      const firstInitial = firstName[0] || '';
      const lastInitial = lastName[0] || '';
      return (firstInitial + lastInitial).toUpperCase() || 'C';
    }
  };
}

export type DecoratedPerson = ReturnType<typeof decoratePerson<Person>>;
